import { useCallback, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { clsx } from "clsx";
import { SearchInput } from "@/components/molecules/SearchInput";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { PageResponse } from "@/types/api";

interface Props<T> {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  loadPage: (page: number, search: string) => Promise<PageResponse<T>>;
  searchPlaceholder: string;
  emptyLabel: string;
  /**
   * Optional id→label cache owned by the parent. Because this list unmounts when
   * the dropdown closes, selected labels are kept here so pinned selections can
   * be rendered on reopen even when the item is not in the loaded page/search.
   */
  labelCache?: Map<string, string>;
}

/** Searchable, paginated, multi-select checkbox list used for the Diary calendar filter groups. */
export function CalendarCheckboxList<T>({
  selectedIds,
  onChange,
  getId,
  getLabel,
  loadPage,
  searchPlaceholder,
  emptyLabel,
  labelCache,
}: Props<T>) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [options, setOptions] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;
  const getIdRef = useRef(getId);
  getIdRef.current = getId;
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;

  // Fallback cache for callers that don't pass one (labels then survive only while mounted).
  const ownLabelCacheRef = useRef(new Map<string, string>());
  const labels = labelCache ?? ownLabelCacheRef.current;

  // Ids selected when the list opened: rendered pinned at the top (labels from the
  // cache) and removed from the paged list body wherever they occur, on any page.
  const pinnedIds = useRef(selectedIds).current;

  const fetchPage = useCallback(
    (pageNum: number, query: string, append: boolean) => {
      setLoading(true);
      loadPageRef.current(pageNum, query)
        .then((res) => {
          const content = res.content ?? [];
          // Remember every loaded label so current/future selections keep their
          // label even after the search or page no longer contains the item.
          for (const item of content) labels.set(getIdRef.current(item), getLabelRef.current(item));
          setOptions((prev) => (append ? [...prev, ...content] : content));
          setHasMore(Boolean(res.hasNext));
          setPage(pageNum);
        })
        .finally(() => setLoading(false));
    },
    [labels],
  );

  useEffect(() => {
    fetchPage(0, debouncedSearch, false);
  }, [debouncedSearch, fetchPage]);

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  const onScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 32) {
      fetchPage(page + 1, debouncedSearch, true);
    }
  };

  const renderRow = (id: string, label: string) => {
    const checked = selectedIds.includes(id);
    return (
      <li key={id}>
        <button
          type="button"
          onClick={() => toggle(id)}
          className={clsx(
            "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
            checked ? "bg-bridge-accent/10 text-bridge-accent font-medium" : "text-bridge-fg hover:bg-bridge-bg",
          )}
        >
          <span
            className={clsx(
              "flex items-center justify-center w-4 h-4 rounded border shrink-0",
              checked ? "bg-bridge-accent border-bridge-accent text-white" : "border-bridge-border/70",
            )}
          >
            {checked && <Check size={12} strokeWidth={3} />}
          </span>
          <span className="truncate flex-1">{label}</span>
        </button>
      </li>
    );
  };

  const bodyOptions = options.filter((o) => !pinnedIds.includes(getId(o)));

  return (
    <div className="flex flex-col">
      <div className="px-2 pb-1.5">
        <SearchInput value={search} onChange={setSearch} placeholder={searchPlaceholder} />
      </div>
      <ul ref={listRef} onScroll={onScroll} className="max-h-40 overflow-auto">
        {pinnedIds.length === 0 && bodyOptions.length === 0 && !loading && (
          <li className="px-3 py-2 text-center text-xs text-bridge-muted">{emptyLabel}</li>
        )}
        {pinnedIds.map((id) => renderRow(id, labels.get(id) ?? id))}
        {bodyOptions.map((o) => renderRow(getId(o), getLabel(o)))}
        {loading && <li className="px-3 py-2 text-center text-xs text-bridge-muted">Loading…</li>}
      </ul>
    </div>
  );
}
